import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { PageHeader } from "@/components/PageHeader";
import {
  apiGetDirectionSummary,
  type DirectionSummary,
} from "@/core/hospitol-api-client";
import { downloadInvoicesCsv, listValidatedInvoices } from "@/core/pharmacy/pharmacy-store";
import { routes } from "@/router/all_routes";

function downloadDirectionSummaryCsv(summary: DirectionSummary): void {
  const rows = [
    ["clé", "valeur"],
    ["tenantSlug", summary.tenantSlug],
    ["generatedAt", summary.generatedAt],
    ["establishment.configured", String(summary.establishment.configured)],
    ["establishment.name", summary.establishment.name],
    ["medicalUnits.total", String(summary.medicalUnits.total)],
    ["medicalUnits.active", String(summary.medicalUnits.active)],
    ["sites.total", String(summary.sites.total)],
    ["sites.active", String(summary.sites.active)],
    ["domains.hospitalStays", String(summary.domains.hospitalStays)],
    ["domains.surgeryCases", String(summary.domains.surgeryCases)],
    ["domains.billingDocuments", String(summary.domains.billingDocuments)],
    ["domains.pharmacyInvoices", String(summary.domains.pharmacyInvoices)],
  ];
  const csv = rows.map((r) => r.map((c) => `"${String(c).replace(/"/g, '""')}"`).join(",")).join("\n");
  const blob = new Blob([csv], { type: "text/csv;charset=utf-8" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = `direction-summary-${summary.tenantSlug}-${Date.now()}.csv`;
  a.click();
  URL.revokeObjectURL(url);
}

type HubCard = {
  title: string;
  description: string;
  to: string;
  cta: string;
};

const HUB_LINKS: HubCard[] = [
  {
    title: "Services hospitaliers",
    description: "Vue d'ensemble des modules opérationnels et revenus.",
    to: routes.hospitalServicesHub,
    cta: "Ouvrir les services",
  },
  {
    title: "Santé financière",
    description: "Indicateurs recettes et performance financière.",
    to: routes.dashboardFinance,
    cta: "Ouvrir finance",
  },
  {
    title: "Performance opérationnelle",
    description: "Flux admissions, urgences et activité.",
    to: routes.dashboardOperations,
    cta: "Ouvrir opérations",
  },
  {
    title: "Paramètres établissement",
    description: "Identité, sites, unités médicales, tarifs.",
    to: routes.adminSettings,
    cta: "Ouvrir paramètres",
  },
  {
    title: "Utilisateurs & rôles",
    description: "Matrice IAM et accès direction.",
    to: routes.adminUsers,
    cta: "Ouvrir utilisateurs",
  },
];

export function DirectionHubPage() {
  const [summary, setSummary] = useState<DirectionSummary | null>(null);
  const [loading, setLoading] = useState(true);
  const invoiceCount = listValidatedInvoices().length;

  useEffect(() => {
    let cancelled = false;
    void apiGetDirectionSummary().then((remote) => {
      if (!cancelled) {
        setSummary(remote);
        setLoading(false);
      }
    });
    return () => {
      cancelled = true;
    };
  }, []);

  return (
    <div>
      <PageHeader
        title="Direction"
        description="Hub de gouvernance — composition des vues Admin, multi-site et tableaux de bord."
        actions={
          <div className="ph-page-header-actions d-flex gap-2 flex-wrap">
            {summary && (
              <button
                type="button"
                className="btn btn-outline-secondary btn-sm"
                onClick={() => downloadDirectionSummaryCsv(summary)}
              >
                Export résumé CSV
              </button>
            )}
            <button
              type="button"
              className="btn btn-outline-secondary btn-sm"
              onClick={() => downloadInvoicesCsv()}
              disabled={invoiceCount === 0}
            >
              Export recettes CSV ({invoiceCount})
            </button>
            <Link to={routes.adminSettings} className="btn btn-outline-primary btn-sm">
              Paramètres
            </Link>
          </div>
        }
      />

      <div className="ph-invoice-sheet mb-4">
        <div className="ph-invoice-section-title">Synthèse établissement</div>
        {loading && <p className="small text-muted mb-0">Chargement…</p>}
        {!loading && !summary && (
          <p className="small text-muted mb-0">
            Synthèse API indisponible — les liens de gouvernance restent accessibles ci-dessous.
          </p>
        )}
        {summary && (
          <div className="row g-3">
            <div className="col-6 col-md-3">
              <div className="small text-muted">Établissement</div>
              <div className="fw-semibold">
                {summary.establishment.name || (summary.establishment.configured ? "Configuré" : "—")}
              </div>
            </div>
            <div className="col-6 col-md-3">
              <div className="small text-muted">Unités actives</div>
              <div className="fw-semibold">
                {summary.medicalUnits.active} / {summary.medicalUnits.total}
              </div>
            </div>
            <div className="col-6 col-md-3">
              <div className="small text-muted">Sites actifs</div>
              <div className="fw-semibold">
                {summary.sites.active} / {summary.sites.total}
              </div>
            </div>
            <div className="col-6 col-md-3">
              <div className="small text-muted">Séjours · Chir. · Factures</div>
              <div className="fw-semibold">
                {summary.domains.hospitalStays} · {summary.domains.surgeryCases} ·{" "}
                {summary.domains.billingDocuments + summary.domains.pharmacyInvoices}
              </div>
            </div>
          </div>
        )}
      </div>

      <div className="row g-3">
        {HUB_LINKS.map((card) => (
          <div key={card.to} className="col-12 col-md-6 col-xl-4">
            <div className="ph-invoice-sheet ph-dash-card h-100 d-flex flex-column">
              <h2 className="h6 fw-semibold mb-2">{card.title}</h2>
              <p className="small text-muted flex-grow-1">{card.description}</p>
              <Link to={card.to} className="btn btn-sm btn-outline-primary align-self-start">
                {card.cta} →
              </Link>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
